import { useEffect, useState } from 'react';

// Markdown lives in public/docs rather than src/ because this is an unejected
// Create React App: webpack treats unknown extensions as assets, so importing
// a .md file yields a URL rather than its text. Fetching keeps the build
// config untouched.
const docUrl = (slug) => `${process.env.PUBLIC_URL}/docs/${slug}.md`;

/**
 * The SPA rewrite that makes /docs/:slug work also means a request for a
 * missing .md file comes back as index.html with a 200. Without this check a
 * bad slug would render the app's own HTML into the page instead of an error.
 */
const looksLikeHtml = (text) => /^\s*<(?:!doctype|html)/i.test(text);

/**
 * Loads one markdown document. Mirrors the loading/error handling used by the
 * scoreboard and league cards: a single status string plus a cancelled flag so
 * a slow response can't set state after unmount.
 */
export default function useDoc(slug) {
  const [markdown, setMarkdown] = useState('');
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setMarkdown('');

    (async () => {
      try {
        const response = await fetch(docUrl(slug));
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        const text = await response.text();
        if (looksLikeHtml(text)) throw new Error(`No document at ${slug}`);
        if (cancelled) return;
        setMarkdown(text);
        setStatus('ready');
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [slug]);

  return { markdown, status };
}
